import React, { useState, useMemo, useEffect, createContext, useContext } from 'react';
import { getPortalTheme, onPortalThemeChange } from './utils/portalTheme';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import Dashboard from './components/Dashboard';

// Color mode context
const ColorModeContext = createContext({ toggleColorMode: () => {} });

export const useColorMode = () => useContext(ColorModeContext);

const PORTAL_LOGIN_URL = 'https://visual.eigentrage.com/login';

const getApiBaseUrl = (): string => {
  const host = window.location.hostname;
  if (host === 'reits.eigentrage.com') {
    return 'https://reits.eigentrage.com';
  }
  if (host.endsWith('.eigentrage.com')) {
    return `${window.location.protocol}//${host}`;
  }
  return `http://${host}:3010`;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const isDevelopment = process.env.NODE_ENV === 'development' &&
                           !process.env.REACT_APP_REQUIRE_AUTH;
      if (isDevelopment) {
        setIsAuthenticated(true);
        return;
      }

      try {
        const baseUrl = getApiBaseUrl();
        const res = await fetch(`${baseUrl}/api/auth-status`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          if (data.authenticated) {
            setIsAuthenticated(true);
            return;
          }
        }
      } catch {
        // Fall through to redirect
      }

      // Not authenticated - redirect to portal
      window.location.href = PORTAL_LOGIN_URL;
    };
    checkAuth();
  }, []);

  // Theme is chosen once in the portal and published as the portal_theme
  // cookie; this dashboard follows it rather than keeping its own preference.
  const [mode, setMode] = useState<'light' | 'dark'>(getPortalTheme());
  // The portal owns the theme; re-read its cookie when this tab regains focus.
  useEffect(() => onPortalThemeChange((next) => setMode(next)), []);


  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          return next;
        });
      },
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'dark'
            ? {
                primary: { main: '#818cf8' },
                secondary: { main: '#a78bfa' },
                background: {
                  default: '#0f172a',
                  paper: '#1e293b',
                },
                text: {
                  primary: '#f1f5f9',
                  secondary: '#94a3b8',
                },
                error: { main: '#f87171' },
                success: { main: '#4ade80' },
                warning: { main: '#fbbf24' },
              }
            : {
                primary: { main: '#667eea' },
                secondary: { main: '#764ba2' },
                background: {
                  default: '#f8f9fa',
                  paper: '#ffffff',
                },
                text: {
                  primary: '#1a202c',
                  secondary: '#4a5568',
                },
              }),
        },
        typography: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                scrollbarWidth: 'thin',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 500,
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 500,
              },
            },
          },
        },
      }),
    [mode]
  );

  // Show loading while checking authentication
  if (isAuthenticated === null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
          <Dashboard />
        </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
